import { Grid, LinearProgress, Button, Box } from '@material-ui/core'
import { useEffect, useState } from 'react'
import { withTranslation, WithTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { Dispatch } from 'redux'
import Talk from 'talkjs'
import { getConversation } from '../../../actions/applications'
import { openDialog } from '../../../actions/dialogs'
import { denyReservable, reserveShowing } from '../../../actions/showings'
import {
  DenyReservableAction,
  ReserveShowingAction,
  OpenDialog
} from '../../../actions/types'
import { selectConversation } from '../../../selectors/applications'
import {
  selectReserve,
  selectReserveLoading
} from '../../../selectors/showings'
import { ApplicationStatus } from '../../../types/applications'
import { ReservableShowings } from '../../../types/showings'
import { ReserveInterface, StateInterface } from '../../../types/states'
import {
  get,
  TalkUser,
  getConversationById,
  initialize,
  addPopup
} from '../../../utils/talkChat'
import { StatusInfo } from './InfoViews'
import NotAvailable from './NotAvailable'
import ReserveView from './ReserveView'
import ApplicationsApi from '../../../services/applications'
import { useParams } from 'react-router-dom'

interface ReserveShowingProps extends WithTranslation {
  conversation: any
  reserve: ReserveInterface
  reserveShowingAction: ReserveShowingAction
  denyReservableAction: DenyReservableAction
  reservableShowings: ReservableShowings | []
  reserveLoading: boolean
  openDialogAction: OpenDialog
  getConversation: any
}

const ReserveShowing = ({
  t,
  conversation,
  reserve,
  reserveShowingAction,
  denyReservableAction,
  reserveLoading,
  openDialogAction,
  getConversation
}: ReserveShowingProps) => {
  const [loading, setLoading] = useState(false) // State for loading
  let chatPopup: Talk.Popup
  const hasErrored = !!reserve?.reserveViewLogic?.error
  const hasReservation = reserve?.reserveData?.reserved
  const status = reserve.reserveData?.application.status
  const { applicationCode } = useParams<RouteParams>();
  type RouteParams = {
    applicationCode: string
  }
  const unreadMessageEvent = () => {
    if (window.talkSession) {
      window.talkSession.unreads.on(
        'change',
        function (unreadConversations: any) {
          if (unreadConversations && unreadConversations.length > 0)
            chatPopup.show()
        }
      )
    }
  }

  const initiateConversation = async () => {
    const selfUserChat: TalkUser = {
      id: reserve.reserveData?.application.applicationId || '',
      name: reserve.reserveData?.application.name || '',
      // If applicant is rejected, make chat read-only via TalkJS role
      role: status === ApplicationStatus.rejected ? 'applicant_readonly' : 'applicant'
    }
    await initialize(selfUserChat)

    const conversation = await getConversationById(
      reserve.reserveData?.application.applicationId || ''
    )

    // Additionally, explicitly set participant access to Read for rejected applicants
    if (status === ApplicationStatus.rejected) {
      try {
        const session = await get()
        if (session) {
          // Prefer session.me if available; fallback to creating a Talk.User again
          // @ts-ignore - TalkJS exposes `me` on the session instance
          const meUser = (session as any).me
          if (meUser && conversation?.setParticipant) {
            conversation.setParticipant(meUser, { access: 'Read' })
          }
        }
      } catch (e) {
        // no-op: failing to set access shouldn't block the flow
        // console.warn('Failed to set TalkJS access to Read:', e)
      }
    }

    return conversation
  }

  const onButtonClick = async () => {
    setLoading(true)
    try {
      await ApplicationsApi.updateApplicationStatusByCode(applicationCode, {
        status: ApplicationStatus.created
      })
    } catch (error) {
      console.error('Failed to update application status', error)
    } finally {
      window.location.reload()
      setLoading(false)
    }
  }

  const showChatPopup = async (conversation: any) => {
    const session = await get()
    chatPopup = session.createPopup()
    chatPopup.select(conversation)
    chatPopup.mount({ show: false })
    addPopup(chatPopup)
  }

  useEffect(() => {
    const checkConversation = async () => {
      if (conversation?.conversation?.conversationData?.custom?.subject) {
        const conversation = await initiateConversation()
        if (conversation) {
          await showChatPopup(conversation)
          unreadMessageEvent()
        }
      }
    }

    checkConversation()
  }, [conversation])

  useEffect(() => {
    if (reserve.reserveData?.application) {
      getConversation(reserve.reserveData?.application?.applicationId)
    }
  }, [reserve.reserveData?.application])

  if (status === ApplicationStatus.rejected) {
    return <NotAvailable t={t} />
  } else if (status === ApplicationStatus.notInterested) {
    return (
      <>
        <NotAvailable t={t} />
        <Box display="flex" justifyContent="center" mt={4}>
          <Button
            variant="contained"
            color="primary"
            onClick={onButtonClick}
            disabled={loading}
          >
            {t('showings.reserve.restoreToCreated')}
          </Button>
        </Box>
      </>
    )
  } else {
    return (
      <Grid container justify="center">
        {reserveLoading ? (
          <LinearProgress />
        ) : (
          !hasErrored &&
          reserve?.reserveData && (
            <ReserveView
              reserve={reserve}
              reserveData={reserve.reserveData}
              t={t}
              reserveShowing={reserveShowingAction}
              denyReservable={denyReservableAction}
              openDialog={openDialogAction}
            />
          )
        )}

        {hasErrored && !hasReservation && <StatusInfo reserve={reserve} />}
      </Grid>
    )
  }
}

const mapStateToProps = (state: StateInterface) => {
  return {
    reserveLoading: selectReserveLoading(state),
    reserve: selectReserve(state),
    conversation: selectConversation(state)
  }
}

const mapDispatchToProps = (dispatch: Dispatch) => ({
  reserveShowingAction: (showingId: string) =>
    dispatch(reserveShowing(showingId)),
  denyReservableAction: (type: 'bestReservable' | 'reservable') =>
    dispatch(denyReservable(type)),
  openDialogAction: (dialogId: string) => dispatch(openDialog(dialogId)),
  getConversation: (applicationId: string) =>
    dispatch(getConversation(applicationId))
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(ReserveShowing))
